import type {
  createDerivedColorsFn,
  createColorAliasesFn,
  ThemeColor,
  createColorComponentAliasesFn,
} from "./types";

/*
Note:
if you add a new color spectrum, please also add it
to the varations type for the `Banner` component
*/
export const createDerivedColors: createDerivedColorsFn = (mode) => {
  return {
    white: "#FFFFFF",
    gray050: "#FBFCFD",
    gray075: "#F0F2F4",
    gray085: "#F2F3F6",
    gray100: "#EDEFF2",
    gray200: "#CCD1D8",
    gray300: "#BCC5CF",
    gray400: "#95A0AC",
    gray500: "#747B86",
    gray600: "#676F79",
    gray700: "#40464E",
    gray800: "#2E3237",
    gray900: "#1D2025",
    gray925: "#17191D",
    gray950: "#131518",
    black: "#000000",

    success100: "#E8FCE8",
    success200: "#B0EEB0",
    success300: "#6CCC6D",
    success400: "#34AD48",
    success500: "#0A8930",
    success600: "#097228",
    success700: "#056121",
    success800: "#034516",
    success900: "#013202",
    error100: "#FCE8E9",
    error200: "#FFB3B5",
    error300: "#FF6669",
    error400: "#FB505B",
    error500: "#E3273F",
    error600: "#C51B31",
    error700: "#9D0C1F",
    error800: "#710513",
    error900: "#4E030D",
    warning100: "#FCF4E8",
    warning200: "#FAE5C6",
    warning300: "#FFBE66",
    warning400: "#FFAA38",
    warning500: "#FF9200",
    warning600: "#E57906",
    warning700: "#CA6902",
    warning800: "#994F00",
    warning900: "#663500",
    info100: "#D9EBFC",
    info200: "#A0CCF8",
    info300: "#61AAF2",
    info400: "#1D8BE2",
    info500: "#0378D4",
    info600: "#0566B3",
    info700: "#034F8C",
    info800: "#003966",
    info900: "#00223D",

    pink100: "#F8DEE6",
    pink200: "#F3C7D4",
    pink300: "#EEAFC3",
    pink400: "#E892AD",
    pink500: "#E27597",
    pink600: "#DD5E85",
    pink700: "#D84673",
    pink800: "#D32F61",
    pink900: "#B82754",
    turquoise100: "#D6F2F5",
    turquoise200: "#ADE5EB",
    turquoise300: "#84D7E1",
    turquoise400: "#5BCAD7",
    turquoise500: "#32BDCD",
    turquoise600: "#2897A4",
    turquoise700: "#1E717B",
    turquoise800: "#144C52",
    turquoise900: "#0A2629",
    lightPurple100: "#E5DDF9",
    lightPurple200: "#D3C5F5",
    lightPurple300: "#C0ADF0",
    lightPurple400: "#AE95EC",
    lightPurple500: "#9473E6",
    lightPurple600: "#825BE2",
    lightPurple700: "#6B3DDD",
    lightPurple800: "#5926D7",
    lightPurple900: "#4F22BF",
    purple100: "#F3E9F6",
    purple200: "#E6D0EC",
    purple300: "#DBBCE3",
    purple400: "#CDA3D9",
    purple500: "#C28FD1",
    purple600: "#B373C5",
    purple700: "#A85FBD",
    purple800: "#9D4BB5",
    purple900: "#8C42A1",
    deepPurple100: "#E6E1F3",
    deepPurple200: "#D6CDEB",
    deepPurple300: "#C2B4E1",
    deepPurple400: "#B19FD8",
    deepPurple500: "#9D86CE",
    deepPurple600: "#8C72C6",
    deepPurple700: "#7859BC",
    deepPurple800: "#6947B1",
    deepPurple900: "#5D3F9D",
    indigo100: "#E0E2F1",
    indigo200: "#CCD0E9",
    indigo300: "#B3B9DE",
    indigo400: "#9FA6D6",
    indigo500: "#848DCA",
    indigo600: "#707BC1",
    indigo700: "#5C68B9",
    indigo800: "#4B58AE",
    indigo900: "#404B95",
    lightBlue100: "#DEF0FC",
    lightBlue200: "#C4E4FA",
    lightBlue300: "#A3D5F7",
    lightBlue400: "#83C6F4",
    lightBlue500: "#69BAF1",
    lightBlue600: "#4FAEEF",
    lightBlue700: "#2698E4",
    lightBlue800: "#1479BC",
    lightBlue900: "#005A97",
    blue100: "#E2EEFC",
    blue200: "#C8DFFA",
    blue300: "#A8CDF6",
    blue400: "#88BBF3",
    blue500: "#6EACF1",
    blue600: "#549DEE",
    blue700: "#3A8FEB",
    blue800: "#1772D6",
    blue900: "#0154AF",
    teal100: "#E8F3F2",
    teal200: "#CEE6E3",
    teal300: "#BBDDD9",
    teal400: "#A3D1CC",
    teal500: "#90C7C1",
    teal600: "#79BCB4",
    teal700: "#4B9D95",
    teal800: "#3D7E77",
    teal900: "#1F6159",
    lightGreen100: "#F0F6E8",
    lightGreen200: "#E0EDCE",
    lightGreen300: "#D3E5BA",
    lightGreen400: "#C3DBA0",
    lightGreen500: "#B6D48C",
    lightGreen600: "#A6CA72",
    lightGreen700: "#759C3B",
    lightGreen800: "#527422",
    lightGreen900: "#3C6106",
    green100: "#E3F0E1",
    green200: "#D1E7CE",
    green300: "#C0DDBB",
    green400: "#A7D0A1",
    green500: "#95C78E",
    green600: "#7FBB76",
    green700: "#6DB263",
    green800: "#5DA653",
    green900: "#539349",
    lime100: "#F8F9E4",
    lime200: "#F1F4CD",
    lime300: "#E9EEB0",
    lime400: "#E1E893",
    lime500: "#DAE37B",
    lime600: "#A4B500",
    lime700: "#8D9723",
    lime800: "#70790A",
    lime900: "#525900",
    yellow100: "#FFFFCC",
    yellow200: "#FFFF99",
    yellow300: "#FFFF66",
    yellow400: "#FFFF4D",
    yellow500: "#FFFF00",
    yellow600: "#EBEB00",
    yellow700: "#CCCC00",
    yellow800: "#B2B200",
    yellow900: "#808000",
    amber100: "#FDF4DA",
    amber200: "#FCECBF",
    amber300: "#FBE39E",
    amber400: "#F9D97C",
    amber500: "#F8D161",
    amber600: "#F7C946",
    amber700: "#B58C17",
    amber800: "#99740A",
    amber900: "#765800",
    orange100: "#FFE4CC",
    orange200: "#FFC999",
    orange300: "#FFBB80",
    orange400: "#FFA85C",
    orange500: "#FF9233",
    orange600: "#FF841A",
    orange700: "#FF7700",
    orange800: "#CC5F00",
    orange900: "#994800",
    brown100: "#F0ECEA",
    brown200: "#E1D9D6",
    brown300: "#D2C6C2",
    brown400: "#C5B7B2",
    brown500: "#B9A8A1",
    brown600: "#A9938B",
    brown700: "#9C847A",
    brown800: "#8C7268",
    brown900: "#7C655C",
    slate100: "#E9EFFB",
    slate200: "#D7E3F7",
    slate300: "#BCC8DE",
    slate400: "#A2AEC4",
    slate500: "#8995AB",
    slate600: "#717D91",
    slate700: "#5B6578",
    slate800: "#454E5F",
    slate900: "#313845",
    redSalmon100: "#F9D9D2",
    redSalmon200: "#F4B2A4",
    redSalmon300: "#EF8D76",
    redSalmon400: "#E96549",
    redSalmon500: "#D43F1B",
    redSalmon600: "#CD3918",
    redSalmon700: "#A23110",
    redSalmon800: "#892510",
    redSalmon900: "#721F0E",
  };
};

export const createColorAliases: createColorAliasesFn = (colors, mode) => {
  const isLightMode = mode === "light";
  const colorByMode = (light: ThemeColor, dark: ThemeColor) =>
    isLightMode ? colors[light] : colors[dark];

  const primary = colorByMode("gray900", "gray050");
  const primaryInverse = colorByMode("gray050", "gray900");
  const secondary = colorByMode("gray700", "gray200");
  const secondaryInverse = colorByMode("gray200", "gray700");
  const tertiary = colorByMode("gray500", "gray300");
  const tertiaryInverse = colorByMode("gray300", "gray500");

  const turquoisePrimary = colorByMode("turquoise600", "turquoise300");
  const redSalmonPrimary = colorByMode("redSalmon400", "redSalmon300");

  const strokeLow = colorByMode("gray300", "gray600");
  const strokeMedium = colorByMode("gray400", "gray500");
  const strokeHigh = colorByMode("gray500", "gray300");
  const strokeContainer = colorByMode("gray600", "gray300");
  const strokeContainerDisabled = colorByMode("gray300", "gray700");

  return {
    primary,
    primaryInverse,
    secondary,
    secondaryInverse,
    pageBackground: colorByMode("gray050", "gray900"),
    link: primary,

    // deprecated color tokens
    tertiary,
    tertiaryInverse,
    neutral: colorByMode("gray600", "gray300"),
    neutralHover: colorByMode("gray700", "gray100"),
    disabledLight: colorByMode("gray400", "gray600"),
    mutedBackgroundGrayscale: colorByMode("gray200", "gray900"),
    mutedTextGrayscale: colorByMode("gray700", "gray400"),
    primaryMuted: primary,
    textPrimary: colorByMode("gray900", "gray050"),
    textPrimaryInverse: colorByMode("gray050", "gray900"),
    textSecondary: colorByMode("gray400", "gray700"),
    raisedSurfaces: colorByMode("white", "gray800"),
    border: strokeLow,

    // new alias values
    knowledge: colorByMode("info500", "info300"),
    info: colorByMode("info500", "info300"),
    infoPrimary: colorByMode("info500", "info300"),
    infoPrimaryInverse: colorByMode("info300", "info500"),
    infoSecondary: colorByMode("info600", "info200"),
    infoMutedBackground: colorByMode("info100", "info800"),
    infoText: colorByMode("info700", "info300"),
    infoTextInverse: colorByMode("info300", "info700"),

    warning: colorByMode("warning500", "warning300"),
    warningPrimary: colorByMode("warning500", "warning300"),
    warningPrimaryInverse: colorByMode("warning300", "warning500"),
    warningSecondary: colorByMode("warning600", "warning200"),
    warningMutedBackground: colorByMode("warning200", "warning900"),
    warningText: colorByMode("warning900", "warning300"),
    warningTextInverse: colorByMode("warning300", "warning900"),

    success: colorByMode("success500", "success300"),
    successPrimary: colorByMode("success500", "success300"),
    successPrimaryInverse: colorByMode("success300", "success500"),
    successSecondary: colorByMode("success600", "success200"),
    successMutedBackground: colorByMode("success100", "success800"),
    successText: colorByMode("success700", "success300"),
    successTextInverse: colorByMode("success300", "success700"),

    error: colorByMode("error500", "error300"),
    errorPrimary: colorByMode("error500", "error300"),
    errorPrimaryInverse: colorByMode("error300", "error500"),
    errorSecondary: colorByMode("error600", "error200"),
    errorMutedBackground: colorByMode("error100", "error800"),
    errorText: colorByMode("error700", "error300"),
    errorTextInverse: colorByMode("error300", "error700"),

    backgroundPrimary: colorByMode("white", "gray950"),
    backgroundSecondary: colorByMode("white", "gray800"),
    backgroundMuted: colorByMode("gray100", "gray700"),
    backgroundMutedSecondary: colorByMode("gray200", "gray600"),
    backgroundContainer: colorByMode("gray050", "gray800"),
    backgroundContainerHover: colorByMode("gray100", "gray700"),
    backgroundRaised: colorByMode("white", "gray800"),
    backgroundDisabled: colorByMode("gray200", "gray600"),
    backgroundDisabledInverse: colorByMode("gray700", "gray200"),

    pinkPrimary: colorByMode("pink600", "pink300"),
    pinkText: colorByMode("pink900", "pink300"),

    pinkMutedBackground: colorByMode("pink100", "pink900"),
    lightPurplePrimary: colorByMode("lightPurple600", "lightPurple300"),
    lightPurpleText: colorByMode("lightPurple800", "lightPurple300"),
    lightPurpleMutedBackground: colorByMode("lightPurple100", "lightPurple900"),

    purplePrimary: colorByMode("purple700", "purple300"),
    purpleText: colorByMode("purple800", "purple300"),
    purpleMutedBackground: colorByMode("purple100", "purple900"),

    deepPurplePrimary: colorByMode("deepPurple500", "deepPurple300"),
    deepPurpleText: colorByMode("deepPurple900", "deepPurple300"),
    deepPurpleMutedBackground: colorByMode("deepPurple100", "deepPurple900"),

    indigoPrimary: colorByMode("indigo500", "indigo300"),
    indigoText: colorByMode("indigo900", "indigo300"),
    indigoMutedBackground: colorByMode("indigo100", "indigo900"),

    lightBluePrimary: colorByMode("lightBlue700", "lightBlue300"),
    lightBlueText: colorByMode("lightBlue900", "lightBlue300"),
    lightBlueMutedBackground: colorByMode("lightBlue100", "lightBlue900"),

    bluePrimary: colorByMode("lightBlue800", "blue300"),
    blueText: colorByMode("blue900", "blue300"),
    blueMutedBackground: colorByMode("blue100", "blue900"),

    turquoisePrimary,
    turquoiseText: colorByMode("turquoise800", "turquoise300"),
    turquoiseMutedBackground: colorByMode("turquoise100", "turquoise800"),

    tealPrimary: colorByMode("teal700", "teal300"),
    tealText: colorByMode("teal900", "teal300"),
    tealMutedBackground: colorByMode("teal100", "teal900"),

    lightGreenPrimary: colorByMode("lightGreen700", "lightGreen300"),
    lightGreenText: colorByMode("lightGreen900", "lightGreen300"),
    lightGreenMutedBackground: colorByMode("lightGreen100", "lightGreen700"),

    greenPrimary: colorByMode("green700", "green300"),
    greenText: colorByMode("green900", "green300"),
    greenMutedBackground: colorByMode("green100", "green900"),

    limePrimary: colorByMode("lime700", "lime300"),
    limeText: colorByMode("lime900", "lime300"),
    limeMutedBackground: colorByMode("lime100", "lime900"),

    yellowPrimary: colorByMode("yellow900", "yellow300"),
    yellowText: colorByMode("gray900", "yellow300"),
    yellowMutedBackground: colorByMode("yellow100", "yellow900"),

    amberPrimary: colorByMode("amber700", "amber300"),
    amberText: colorByMode("amber900", "amber300"),
    amberMutedBackground: colorByMode("amber100", "amber900"),

    orangePrimary: colorByMode("orange800", "orange300"),
    orangeText: colorByMode("orange900", "orange300"),
    orangeMutedBackground: colorByMode("orange100", "orange900"),

    redSalmonPrimary,
    redSalmonText: colorByMode("redSalmon700", "redSalmon300"),
    redSalmonMutedBackground: colorByMode("redSalmon100", "redSalmon900"),

    brownPrimary: colorByMode("brown700", "brown300"),
    brownText: colorByMode("brown900", "brown300"),
    brownMutedBackground: colorByMode("brown100", "brown900"),

    slatePrimary: colorByMode("slate500", "slate300"),
    slateText: colorByMode("slate800", "slate300"),
    slateMutedBackground: colorByMode("slate100", "slate900"),

    textTitle: colorByMode("gray900", "gray050"),
    textTitleInverse: colorByMode("gray050", "gray900"),
    textBody: colorByMode("gray800", "gray100"),
    textBodyInverse: colorByMode("gray100", "gray800"),
    textSubtitle: colorByMode("gray700", "gray200"),
    textSubtitleInverse: colorByMode("gray200", "gray700"),
    textLabel: colorByMode("gray600", "gray300"),
    textLabelInverse: colorByMode("gray300", "gray600"),
    textCaption: colorByMode("gray600", "gray300"),
    textCaptionInverse: colorByMode("gray300", "gray600"),
    textDisabled: colorByMode("gray400", "gray700"),

    externalLinksTurquoise: turquoisePrimary,
    externalLinksRedSalmon: redSalmonPrimary,

    strokeLow,
    strokeMedium,
    strokeHigh,
    strokeContainer,
    strokeContainerDisabled,

    borderLow: strokeLow,
    borderMedium: strokeMedium,
    borderHigh: strokeHigh,
    borderContainer: strokeContainer,
    borderContainerDisabled: strokeContainerDisabled,

    iconTier1: colorByMode("gray900", "gray050"),
    iconTier2: colorByMode("gray800", "gray100"),
    iconTier3: colorByMode("gray700", "gray200"),
    iconTier4: colorByMode("gray600", "gray400"),
    iconTier1Inverse: colorByMode("gray050", "gray900"),
    iconTier2Inverse: colorByMode("gray100", "gray800"),
    iconTier3Inverse: colorByMode("gray200", "gray700"),
    iconTier4Inverse: colorByMode("gray400", "gray600"),
    iconDisabled: colorByMode("gray400", "gray700"),
  };
};

export const createColorComponentAliases: createColorComponentAliasesFn = (
  colors,
  aliases,
  mode,
) => {
  const isLightMode = mode === "light";
  const colorByMode = (light: ThemeColor, dark: ThemeColor) =>
    isLightMode ? colors[light] : colors[dark];

  return {
    switchActiveBackground: colorByMode("gray900", "white"),
    switchInactiveBackground: colorByMode("gray300", "gray400"),
    switchDisabledBackground: colorByMode("gray400", "gray500"),
    switchHandle: colorByMode("gray050", "gray900"),
    switchHandleDisabled: colorByMode("gray200", "gray700"),

    sliderFill: colorByMode("gray300", "gray400"),
    sliderFillDisabled: colorByMode("gray400", "gray700"),
    sliderTrack: colorByMode("gray300", "gray900"),
    sliderTrackDisabled: colorByMode("gray200", "black"),
    sliderHandleActive: aliases.primary,
    sliderDisabledHandle: colorByMode("gray200", "gray900"),

    buttonLongPressTransition: colorByMode("gray600", "gray400"),
    buttonLongPressTransitionError: colorByMode("error700", "error400"),

    // used for default buttons
    buttonTextPrimary: aliases.primaryInverse,
    buttonTextPrimaryHover: aliases.primaryInverse,
    // used for outlined, muted, and plain buttons
    buttonTextPrimaryInverse: aliases.primary,
    buttonTextPrimaryInverseHover: aliases.secondary,
    buttonTextDisabled: aliases.textDisabled,
    // used for default buttons
    buttonTextDestructive: aliases.primaryInverse,
    buttonTextDestructiveHover: aliases.primaryInverse,
    // used for outlined, muted, and plain buttons
    buttonTextDestructiveInverse: aliases.errorText,
    buttonTextDestructiveInverseHover: colorByMode("error800", "error200"),

    buttonBackgroundPrimary: aliases.primary,
    buttonBackgroundPrimaryHover: aliases.secondary,
    buttonBackgroundDestructive: aliases.errorPrimary,
    buttonBackgroundDestructiveHover: aliases.errorSecondary,
    buttonBackgroundDisabled: aliases.backgroundDisabled,
    buttonBackgroundMuted: aliases.backgroundMuted,
    buttonBackgroundMutedHover: isLightMode ? "#F2F4F8" : "#414953",

    buttonStrokePrimary: aliases.primary,
    buttonStrokePrimaryHover: aliases.secondary,
    buttonStrokeDisabled: aliases.strokeContainerDisabled,
    buttonStrokeDestructive: aliases.errorPrimary,
    buttonStrokeDestructiveHover: aliases.errorSecondary,

    buttonIconPrimary: aliases.iconTier1Inverse,
    buttonIconPrimaryHover: aliases.iconTier3Inverse,
    buttonIconInverse: aliases.iconTier1,
    buttonIconInverseHover: aliases.iconTier3,
    buttonIconDisabled: aliases.iconDisabled,
    buttonIconDestructive: aliases.primaryInverse,
    buttonIconDestructiveHover: aliases.primaryInverse,
    buttonIconDestructiveInverse: aliases.errorText,
    buttonIconDestructiveInverseHover: colorByMode("error800", "error200"),

    stepperBackgroundContainer: colorByMode("gray050", "black"),

    tooltipBackground: colorByMode("gray800", "gray600"),

    deviceBadgeBackground: colorByMode("gray075", "black"),
    deviceThermostatCardControlBackground: colorByMode("gray075", "gray900"),
    deviceBadgeAdornmentStroke: colorByMode("white", "black"),
    deviceBadgeAdornmentIcon: colorByMode("white", "black"),
    deviceBadgeVectorStroke: colorByMode("gray700", "gray200"),
    deviceBadgeVectorStrokeSecondary: colorByMode("gray200", "gray600"),
    deviceBadgeVectorFill: colorByMode("gray200", "gray600"),
    deviceBadgeVectorFillSecondary: colorByMode("white", "black"),
    deviceBadgeVectorThermostatTemp: colorByMode("gray700", "gray200"),

    skeletonPrimary: aliases.backgroundMuted,
    skeletonSecondary: aliases.backgroundMutedSecondary,

    buttonGroupTextDisabled: colorByMode("gray400", "gray600"),

    inputBackground: aliases.backgroundContainer,
    inputBackgroundDisabled: aliases.backgroundDisabled,
    inputStroke: aliases.strokeContainer,
    inputStrokeFocused: aliases.primary,
    inputStrokeError: aliases.errorPrimary,
    inputStrokeDisabled: aliases.strokeContainerDisabled,
    inputSegmentedControlBackground: aliases.backgroundMuted,
    inputSegmentedControlBackgroundDisabled: colorByMode("gray300", "gray500"),
    inputSegmentedControlStroke: aliases.backgroundMuted,
    inputSegmentedControlStrokeFocused: aliases.primary,
    inputSegmentedControlStrokeDisabled: colorByMode("gray300", "gray500"),
    inputSegmentedControlText: aliases.textBody,
    inputSegmentedControlTextDisabled: aliases.textDisabled,
    inputClearAdornment: aliases.iconTier4,
    inputAdornment: aliases.iconTier4,
    inputAdornmentDisabled: aliases.iconDisabled,
    inputAdornmentError: aliases.errorPrimary,
    inputLabelText: aliases.textLabel,
    inputLabelTextError: aliases.errorText,
    inputLabelTextDisabled: aliases.textDisabled,
    inputText: aliases.textBody,
    inputTextDisabled: aliases.textDisabled,
    inputPlaceholderText: aliases.textLabel,
    inputPlaceholderTextDisabled: aliases.textDisabled,
    inputHelperText: aliases.textCaption,
    inputHelperTextError: aliases.errorText,

    modalBackdrop: colors.black,

    dialogBackground: aliases.backgroundRaised,
    dialogTextTitle: aliases.textTitle,
    dialogTextDescription: aliases.textBody,
  };
};
